import React from 'react';
import { getOnboardingUrl } from "../../utils/portalUrl";
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../../Components/SEO/SEO';
import { FEATURES_META } from '../../seo/pageMeta';
import { FaCalendarCheck, FaUsers, FaGlobe, FaVideo, FaBell, FaUserFriends, FaCreditCard, FaCalendarAlt, FaBullhorn, FaTag, FaImages, FaIdCard, FaClipboardList, FaUserShield, FaQrcode, FaMobileAlt, FaMoneyBillWave, FaStar, FaBoxOpen, FaBullseye,
  FaTooth, FaCrosshairs, FaSyringe, FaFolderOpen, FaProjectDiagram, FaFileSignature, FaGraduationCap, FaStore, FaCashRegister, FaRobot, FaHotel, FaDoorOpen, FaUsersCog, FaCommentDots } from 'react-icons/fa';
import Navbar from '../../Components/Navbar/Navbar';
import Footer from '../../Components/Footer/Footer';
import styles from './FeaturesOverview.module.css';
import { isHiddenFeatureKey } from '../../config/tempCrmPivot';

const FEATURE_ITEMS = [
  { key: 'booking',       path: '/features/online-booking',  Icon: FaCalendarCheck, color: '#FE4E00' },
  { key: 'appointments',  path: '/features/appointments',    Icon: FaCalendarAlt,   color: '#FE4E00' },
  { key: 'crm',           path: '/features/crm',             Icon: FaUsers,         color: '#203052' },
  { key: 'notifications', path: '/features/notifications',   Icon: FaBullhorn,      color: '#203052' },
  { key: 'website',       path: '/features/website',         Icon: FaGlobe,         color: '#0ea5e9' },
  { key: 'profile',       path: '/features/profile',         Icon: FaIdCard,        color: '#8b5cf6' },
  { key: 'zoom',          path: '/features/zoom',            Icon: FaVideo,         color: '#2d8cff' },
  { key: 'reminders',     path: '/features/reminders',       Icon: FaBell,          color: '#f59e0b' },
  { key: 'team',          path: '/features/team',            Icon: FaUserFriends,   color: '#10b981' },
  { key: 'discounts',     path: '/features/discounts',       Icon: FaTag,           color: '#10b981' },
  { key: 'payments',      path: '/features/payments',        Icon: FaCreditCard,    color: '#8b5cf6' },
  { key: 'gallery',       path: '/features/gallery',         Icon: FaImages,        color: '#0ea5e9' },
  { key: 'services',      path: '/features/services',        Icon: FaClipboardList, color: '#FE4E00' },
  { key: 'calendar',      path: '/features/calendar',        Icon: FaCalendarAlt,   color: '#0ea5e9' },
  { key: 'roles',         path: '/features/roles',           Icon: FaUserShield,    color: '#10b981' },
  { key: 'qr',            path: '/features/qr',              Icon: FaQrcode,        color: '#203052' },
  { key: 'app',           path: '/features/app',             Icon: FaMobileAlt,     color: '#8b5cf6' },
  // Finance, Ratings, Product store and Marketing all EXIST today — shown as full,
  // available features (no "coming soon"). Google Calendar + AI are intentionally
  // omitted from the grid for now.
  { key: 'finance',      path: '/features/finance',         Icon: FaMoneyBillWave,  color: '#10b981' },
  { key: 'reviews',      path: '/features/reviews',         Icon: FaStar,           color: '#f59e0b' },
  { key: 'products',     path: '/features/products',        Icon: FaBoxOpen,        color: '#FE4E00' },
  { key: 'marketing',    path: '/features/marketing',       Icon: FaBullseye,       color: '#203052' },

  // ── Published by the 2026-09 expansion ────────────────────────────────────
  { key: 'dental-charting', path: '/features/dental-charting', Icon: FaTooth,          color: '#0ea5e9' },
  { key: 'treatment-areas', path: '/features/treatment-areas', Icon: FaCrosshairs,     color: '#10b981' },
  { key: 'injectables',     path: '/features/injectables',     Icon: FaSyringe,        color: '#8b5cf6' },
  { key: 'records',         path: '/features/records',         Icon: FaFolderOpen,     color: '#203052' },
  { key: 'treatment-plans', path: '/features/treatment-plans', Icon: FaProjectDiagram, color: '#0891b2' },
  { key: 'agreements',      path: '/features/agreements',      Icon: FaFileSignature,  color: '#059669' },
  { key: 'courses',         path: '/features/courses',         Icon: FaGraduationCap,  color: '#7c3aed' },
  { key: 'shop',            path: '/features/shop',            Icon: FaStore,          color: '#FE4E00' },
  { key: 'pos',             path: '/features/pos',             Icon: FaCashRegister,   color: '#f59e0b' },
  { key: 'auto-ads',        path: '/features/auto-ads',        Icon: FaRobot,          color: '#203052' },
  { key: 'hospitality',     path: '/features/hospitality',     Icon: FaHotel,          color: '#0ea5e9' },
  { key: 'rooms',           path: '/features/rooms',           Icon: FaDoorOpen,       color: '#16a34a' },
  { key: 'group-booking',   path: '/features/group-booking',   Icon: FaUsersCog,       color: '#d946a8' },
  { key: 'messages',        path: '/features/messages',        Icon: FaCommentDots,    color: '#25D366' },
];

/**
 * Groups for the grid.
 *
 * Thirty-five flat cards is a search box with extra steps, not a grid a visitor
 * reads. Membership is declared here and the grid renders group by group; any
 * feature not named below falls into the last group, so adding a feature can
 * never make it disappear from the page.
 */
const GROUPS = [
  { id: 'booking',  keys: ['booking', 'appointments', 'calendar', 'group-booking', 'rooms', 'services'] },
  { id: 'clients',  keys: ['crm', 'records', 'treatment-plans', 'agreements', 'notifications', 'reminders', 'messages'] },
  { id: 'clinical', keys: ['dental-charting', 'treatment-areas', 'injectables'] },
  { id: 'money',    keys: ['payments', 'pos', 'shop', 'products', 'finance', 'discounts'] },
  { id: 'growth',   keys: ['website', 'profile', 'gallery', 'reviews', 'marketing', 'auto-ads', 'qr'] },
  { id: 'ops',      keys: ['team', 'roles', 'app', 'zoom', 'courses', 'hospitality'] },
];

const FeaturesOverview = () => {
  const { t } = useTranslation();
  const visibleItems = FEATURE_ITEMS.filter(({ key }) => !isHiddenFeatureKey(key));

  // Anything not named in a group lands in the last one, so a newly added
  // feature can never vanish from this page for want of a group entry.
  const grouped = GROUPS.map((g, i) => ({
    id: g.id,
    items: visibleItems.filter((item) => (
      GROUPS.some((gg) => gg.keys.includes(item.key))
        ? g.keys.includes(item.key)
        : i === GROUPS.length - 1
    )),
  })).filter((g) => g.items.length);

  return (
    <div className={styles.page}>
      <SEO {...FEATURES_META} />
      <Navbar />

      <section className={styles.hero}>
        <div className="container">
          <h1 data-aos="fade-up">{t('features.overview.heroTitle')}</h1>
          <p data-aos="fade-up" data-aos-delay="100">{t('features.overview.heroSubtitle')}</p>
          <a
            href={getOnboardingUrl("cta")}
            className={styles.heroCta}
            data-aos="fade-up"
            data-aos-delay="200"
          >
            {t('features.overview.cta')}
          </a>
        </div>
      </section>

      <section className={styles.grid}>
        <div className="container">
          {grouped.map((group) => (
            <div key={group.id} className={styles.group}>
              <h2 className={styles.groupTitle} data-aos="fade-up">
                {t(`features.groups.${group.id}`)}
              </h2>
              <div className={styles.cardsGrid}>
                {group.items.map(({ key, path, Icon, color, comingSoon }) => (
                  <Link to={path} key={key} className={`${styles.card} ${comingSoon ? styles.cardComingSoon : ''}`} data-aos="fade-up" data-aos-duration="500">
                    <div className={styles.cardIcon} style={{ backgroundColor: color + '18', color }}>
                      <Icon />
                    </div>
                    <h3>
                      {t(`features.cards.${key}.title`)}
                      {comingSoon && <span className={styles.comingSoonBadge}>{t('nav.comingSoon')}</span>}
                    </h3>
                    <p>{t(`features.cards.${key}.desc`)}</p>
                    <span className={styles.learnMore}>{t('features.common.learnMore')} →</span>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className={styles.ctaSection}>
        <div className="container">
          <h2>{t('features.common.ctaTitle')}</h2>
          <p>{t('features.common.ctaSubtitle')}</p>
          <a
            href={getOnboardingUrl("cta")}
            className={styles.ctaButton}
          >
            {t('features.common.cta')}
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FeaturesOverview;
